/**
 * GoogleAnalyticsService.js
 * Created on 21-07-2021
 * 
 * @author Henrique Campos, Reinier Feijen for Ranj
 **/
export default class GoogleAnalyticsService {
	constructor() {
		this._isInitialized = false;
		this._callQueue = [];
		// start interval for replenishing hit quota
		this._hitCount = 0;
		this._hitsRemaining = HITS_INITIAL;
		this._enabled = false;

		this._persistentValues = {};
	}

	/**
	 * Initialize the service once at startup.
	 * @param {string} measurementId As obtained for GA property.
	 * @param {boolean} enabled Sets if service is enabled.
	 * @param {boolean} anonymizeIp Sets if the IP tracking is anonymous. In 99.9% of cases the default 'true' is required.
	 * @param {Object} persistentValues Sets values that will be sent with every event.
	 */
	initialize(measurementId, enabled = true, anonymizeIp = true, persistentValues = {}) {
		this._measurementId = measurementId;
		this.enabled = enabled;
		this._persistentValues = persistentValues;

		// Initialize gtag.
		this.gtag = window.gtag;
		this.gtag(GA_CONFIG, measurementId, persistentValues);

		// Anonymize ip after creating the tracker else the setting is ignored.
		this.setAnonymizeIp(anonymizeIp);
		this._isInitialized = true;

		// Start replenish cycle.
		this._replenishInterval = setInterval(this._replenish.bind(this), HITS_REPLENISH_INTERVAL);
	}

	destroy() {
		if (this._replenishInterval) {
			clearTimeout(this._replenishInterval);
		}
	}

	/**
	 * En/disable service
	 * @param {Boolean} value
	 */
	set enabled(value) {
		this._enabled = value;

		console.log(`[GoogleAnalyticsService] Enabled: `, value);
		// See: https://developers.google.com/analytics/devguides/collection/ga4/disable-analytics
		window[`ga-disable-${this._measurementId}`] = !this._enabled;
	}

	get enabled() {
		return this._enabled;
	}

	/**
	 * En/disable ip anonymization. This is turned on by default in initialize()
	 * @param {Boolean} value
	 */
	setAnonymizeIp(value) {
		this.setPersistentValue('anonymize_ip', value);

		// See: https://developers.google.com/analytics/devguides/collection/gtagjs/ip-anonymization
		// NB: This executes immediately and not queue
		this._execute(GA_CONFIG, this._measurementId, this._persistentValues);
	}

	/**
	 * Set the user ID of signed-in user
	 * @param {String} value User ID of signed-in user
	 * @returns {undefined}
	 */
	setUserID(value) {
		// See: https://developers.google.com/analytics/devguides/collection/gtagjs/cookies-user-id#set_user_id
		this._queue(GA_CONFIG, this._measurementId, {
			user_id: value
		});
	}

	/**
	 * Set property value to the user that can be used to describe segments of your user base
	 * (e.g., language, environment, etc).
	 * 
	 * This should be configured in the GA Custom Definitions with 'User' scope.
	 * 
	 * @param {string} property 
	 * @param {string} value 
	 */
	setUserProperty(property, value) {
		// See: https://developers.google.com/analytics/devguides/collection/ga4/user-properties
		this._queue(GA_SET, NAME_USER_PROPERTIES, {
			[property]: value
		});
	}

	/**
	 * Set property value that will be sent with every event.
	 * 
	 * This should be configured in the GA Custom Definitions with 'Event' scope.
	 * 
	 * @param {string} property property name
	 * @param {string} value value to set for the property
	 */
	setPersistentValue(property, value) {
		// Save persistent values to object as each gtag call will overwrite all persistent values.
		this._persistentValues[property] = value;

		// See: https://developers.google.com/analytics/devguides/collection/ga4/persistent-values
		this._queue(GA_CONFIG, this._measurementId, this._persistentValues);
	}

	/**
	 * Clears a property value that was previously sent with every event.
	 * @param {string} property property name
	 */
	clearPersistentValue(property) {
		if (this._persistentValues.hasOwnProperty(property)) {
			delete this._persistentValues[property];
		}

		// See: https://developers.google.com/analytics/devguides/collection/ga4/persistent-values
		this._queue(GA_CONFIG, this._measurementId, this._persistentValues);
	}

	/**
	 * Send event
	 * 
	 * https://developers.google.com/analytics/devguides/collection/ga4/events
	 * 
	 * @param {string} name
	 * @param {Object} params
	 * @param {Function} callback
	 */
	sendEvent(name, params = {}, callback = null) {
		if (callback) {
			// add callback
			// See: https://developers.google.com/analytics/devguides/collection/gtagjs/sending-data#implement_event_callback_functions
			params.event_callback = callback;
		}

		this._queue(GA_EVENT, name, params);
	}

	/**
	 * Send an event for exception tracking
	 * 
	 * @param {string} description Error message, max 150 chars.
	 * @param {boolean} isFatal Boolean indicating if this was a fatal exception.
	 * @param {Function} callback
	 */
	sendException(description, isFatal) {
		// See: https://developers.google.com/analytics/devguides/collection/ga4/exceptions
		this._queue(GA_EVENT, NAME_EXCEPTION, {
			description: description,
			fatal: isFatal
		});
	}

	/**
	 * 
	 * @param {string} method
	 * @param {string} name
	 * @param {Object} params
	 */
	_execute(method, name, params) {
		const decoratedParams = {
			...params,
			...this._persistentValues,
		};

		//adjust hit counters
		this._hitsRemaining--;
		//check session limit
		this._hitCount++;
		if (this._hitCount >= HITS_PER_SESSION_LIMIT) {
			console.warn(`[GoogleAnalyticsV4Service] ${method} ${name} ${decoratedParams} send ignored! Session limit reached (${HITS_PER_SESSION_LIMIT}). Further hits will be ignored.`);
		}

		console.log(`[GoogleAnalyticsV4Service] Send ${method} ${name}`, decoratedParams);
		this.gtag(method, name, decoratedParams);
	}

	/**
	 * 
	 * @param {string} method
	 * @param {string} name
	 * @param {Object} params
	 */
	_queue(method, name, params) {
		this._callQueue.push([method, name, params]);
		this._dequeue();
	}

	_dequeue() {
		if (this.enabled && this._isInitialized && this._callQueue.length > 0 && this._hitsRemaining > 0) {
			var args = this._callQueue.shift();
			this._execute(...args);
		}
	}

	_replenish() {
		this._hitsRemaining = Math.min(HITS_INITIAL, this._hitsRemaining + HITS_REPLENISH_INCREMENT);
		//console.log(TAG+"._replenish "+this._hitsRemaining);
		this._dequeue();
	}
}

//String literals used by GA
const GA_CONFIG = "config", GA_EVENT = "event", GA_SET = "set";
const NAME_USER_PROPERTIES = "user_properties", NAME_EXCEPTION = "exception";

//Constants for managing limits
//
// See:  https://developers.google.com/analytics/devguides/collection/gtagjs/limits-quotas
// 10 million hits per month per property
// 500 hits per session
// "Each gtag.js and analytics.js tracker object starts with 20 hits that are replenished at a rate of 2 hits per second."
// Hits are ignored above this limit. Callback are executed as if the events were properly sent and received though!
// There is no way to handle 'errors' of this kind.
// That's why I've implemented our own bookkeeping of remaining event hits. 
// Drawback of this is that the queueing and scheduling cause the timestamps of the hits to be inaccurate.
// This inaccuracy increases with the length of the queue of course.
// Best practice is to keep the amount of hits low. Be selective.

const HITS_REPLENISH_INTERVAL = 1050; //actually 1000, + safety margin
const HITS_REPLENISH_INCREMENT = 2;
const HITS_INITIAL = 18; // actually 20, - safety margin
const HITS_PER_SESSION_LIMIT = 500;